import { useState, type MouseEvent } from "react";
import { useTranslation } from "react-i18next";
import { Divider, Fab, Popover, Tooltip, Typography } from "@mui/material";
import { Box } from "@mui/system";
import { faQuestionCircle } from "@fortawesome/pro-light-svg-icons";

import { FontAwesomeSvgIcon } from "../../ui/FontAwesomeSvgIcon";
import { DRAW_TOOLS } from "./drawingTools";

/**
 * MapDrawingHelp
 *
 * Question-mark button that opens a short primer on the drawing tools. The
 * gestures differ per tool (click-to-click for circle and rectangle, hold-and
 * -drag for the lasso) and are not discoverable from the icons alone, so this
 * sits next to the toolbar rather than in external documentation.
 */
export function MapDrawingHelp() {
  const { t } = useTranslation(["mapview"]);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const open = Boolean(anchorEl);

  return (
    <Box m={2}>
      <Tooltip title={t("tooltips.tooltip_drawing_help")} placement="right">
        <Fab
          size="small"
          color="secondary"
          onClick={(event: MouseEvent<HTMLButtonElement>) => setAnchorEl(event.currentTarget)}
          data-testid="drawing-help"
          aria-label={t("tooltips.tooltip_drawing_help")}
        >
          <FontAwesomeSvgIcon icon={faQuestionCircle} />
        </Fab>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        PaperProps={{ sx: { maxWidth: 340, p: 2 } }}
      >
        <Typography variant="titleMedium" color="text.primary">
          {t("text_draw_help_title")}
        </Typography>

        <Box sx={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: 1, mt: 1.5 }}>
          {DRAW_TOOLS.map(({ mode, icon, labelKey, helpKey }) => (
            <Box key={mode} sx={{ display: "contents" }}>
              <FontAwesomeSvgIcon icon={icon} fontSize="small" sx={{ mt: 0.25 }} />
              <Typography variant="bodySmall" color="text.secondary">
                <Box component="span" sx={{ color: "text.primary", fontWeight: "medium" }}>
                  {`${t(labelKey)} — `}
                </Box>
                {t(helpKey)}
              </Typography>
            </Box>
          ))}
        </Box>

        <Divider sx={{ my: 1.5 }} />

        <Typography variant="bodySmall" color="text.secondary" component="p">
          {t("text_draw_help_single_shape")}
        </Typography>
        <Typography variant="bodySmall" color="text.secondary" component="p" sx={{ mt: 1 }}>
          {t("text_draw_help_filtering")}
        </Typography>
      </Popover>
    </Box>
  );
}
